import type { ReactNode } from 'react'
import { Buildings, CalendarBlank, Check, EnvelopeSimple, MapPin, Phone, Tag, Truck, UserCircle, X } from '@/design-system/icons'
import { Money } from '@phosphor-icons/react'
import { formatNumber } from '@/i18n'
import { parseDispatchDate } from '@/domain/dates'
import { cn, type IconComponent, type Tone } from './primitives'

/*
 * Smart cells: every generic table (Dispatch resources, native modules) renders values by what the field *means*,
 * not only by its JS type — names get an avatar, phones/cities/branches an icon, statuses a coloured pill.
 */

const NAME_KEY = /^(name|fname|fnamea|englishname|arabicname|clientname|accountname|shippername|subaccountname|runnername|playername|consignee|consigneename|recipientname|displayname|dispalyname|customername|drivername|employeename|title|sendername|username)$/i
export const isNameKey = (key: string) => NAME_KEY.test(key.replace(/[_\s]/g, ''))
const PHONE_KEY = /(phone|mobile|tel|contactno|whatsapp)/i
const CITY_KEY = /(city|zone|area|governorate|region|district)(name)?(a|e)?$/i
const BRANCH_KEY = /branch(e)?(name)?(a|e)?$/i
const TAG_KEY = /(pricelist|service|product|shipmenttype|category|typename|type|role)(name)?(a|e)?$/i
const STATUS_KEY = /(status|state)(name)?(a|e)?$|^(result|resultname|stage)$/i
export const isStatusKey = (key: string) => STATUS_KEY.test(key.replace(/[_\s]/g, ''))
const EMAIL_KEY = /mail/i
const USER_KEY = /^(createdby|createdbyname|postedby|updatedby|user|username)$/i
const RUNNER_KEY = /(runner|driver|courier|player)(name)?(a|e)?$/i

const SUCCESS = /(deliver|تسليم|تم التسليم|active|نشط|مفعل|success|posted|مرحل|received|مستلم|paid|مدفوع|done|completed|closed|approved|معتمد|collected|محصل|ok)/i
const DANGER = /(return|مرتجع|رفض|reject|cancel|ملغ|lost|مفقود|damag|تالف|fail|inactive|غير نشط|locked|مقفول|expired|منتهي|error|overdue|متأخر)/i
const WARNING = /(pending|معلق|new|جديد|postpon|مؤجل|hold|انتظار|wait|draft|مسودة|review|مراجعة|partial|جزئي)/i
const INFO = /(out for|جاري|progress|transit|في الطريق|assigned|مسند|pickup|picked|استلام|dispatch|خرج|open|مفتوح|loading|departed)/i

export function toneOf(text: string): Tone {
  if (DANGER.test(text)) return 'danger'
  if (SUCCESS.test(text)) return 'success'
  if (INFO.test(text)) return 'info'
  if (WARNING.test(text)) return 'warning'
  return 'neutral'
}

const pillTone: Record<Tone, string> = {
  neutral: 'bg-neutral-soft text-fg-muted',
  brand: 'bg-brand-soft text-brand-text',
  accent: 'bg-accent-soft text-accent',
  info: 'bg-info-soft text-info',
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  danger: 'bg-danger-soft text-danger',
}

export function StatusPill({ children, tone }: { children: ReactNode; tone?: Tone }) {
  const t = tone ?? toneOf(String(children ?? ''))
  return (
    <span className={cn('inline-flex h-6 max-w-full items-center gap-1.5 truncate rounded-md px-2.5 text-xs font-semibold', pillTone[t])}>
      <span className="size-1.5 shrink-0 rounded-full bg-current" aria-hidden />
      <span className="truncate" title={typeof children === 'string' ? children : undefined}>{children}</span>
    </span>
  )
}

const AVATAR_TONES = ['bg-info-soft text-info', 'bg-accent-soft text-accent', 'bg-success-soft text-success', 'bg-warning-soft text-warning', 'bg-neutral-soft text-fg-strong']

export function Avatar({ name, size = 34 }: { name: string; size?: number }) {
  const clean = name.trim()
  const letter = (clean.match(/[\p{L}\p{N}]/u)?.[0] ?? '?').toUpperCase()
  let h = 0
  for (const ch of clean) h = (h * 31 + ch.charCodeAt(0)) >>> 0
  return (
    <span className={cn('flex shrink-0 items-center justify-center rounded-full text-sm font-bold', AVATAR_TONES[h % AVATAR_TONES.length])} style={{ width: size, height: size }} aria-hidden>
      {letter}
    </span>
  )
}

export function NameCell({ name, sub }: { name: string; sub?: string }) {
  return (
    <span className="block min-w-0 leading-tight">
      <span className="block truncate font-semibold text-fg-strong" title={name}>{name}</span>
      {sub && <span className="mt-0.5 block truncate text-xs text-fg-subtle" title={sub}>{sub}</span>}
    </span>
  )
}

export function IconText({ icon: I, children, mono, dir }: { icon: IconComponent; children: ReactNode; mono?: boolean; dir?: 'ltr' | 'rtl' }) {
  return (
    <span className="flex min-w-0 items-center gap-2">
      <I size={16} className="shrink-0 text-fg-muted" aria-hidden />
      <span className={cn('truncate', mono && 'tabular')} dir={dir} title={typeof children === 'string' ? children : undefined}>{children}</span>
    </span>
  )
}

export function DateCell({ value, locale, withTime, bare }: { value: unknown; locale: 'ar' | 'en'; withTime: boolean; bare?: boolean }) {
  const d = parseDispatchDate(value as string)
  if (!d) return <span className="text-fg-subtle">—</span>
  const date = d.toLocaleDateString(locale === 'ar' ? 'ar-EG-u-nu-latn' : 'en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  const time = d.toLocaleTimeString(locale === 'ar' ? 'ar-EG-u-nu-latn' : 'en-GB', { hour: '2-digit', minute: '2-digit' })
  // `bare` is for the phone card, where the row already carries its own icon and label.
  if (bare) return <span className="tabular block truncate">{withTime ? `${date} · ${time}` : date}</span>
  return (
    <span className="flex min-w-0 items-center gap-2">
      <CalendarBlank size={16} className="shrink-0 text-fg-muted" aria-hidden />
      <span className="min-w-0 leading-tight">
        <span className="block truncate">{date}</span>
        {withTime && <span className="block truncate text-xs text-fg-subtle">{time}</span>}
      </span>
    </span>
  )
}

/** Money the way the runner app shows it: banknote icon, Latin digits, then the pound sign. Used for every money cell in every table. */
export function MoneyText({ value, className, size = 14 }: { value: unknown; className?: string; size?: number }) {
  const n = Number(value) || 0
  return (
    <span className={cn('tabular inline-flex items-center gap-1 font-semibold text-fg-strong', className)} dir="ltr">
      <Money size={size} weight="duotone" className="keep-color shrink-0 text-success" aria-hidden />
      {n.toLocaleString('en-US', { maximumFractionDigits: 2 })} <span className="text-fg-muted">ج</span>
    </span>
  )
}

/**
 * A yes / no as quiet text with a small check or x — never a big round pill (owner, 2026-10-21: «الديزاين الغريب ده
 * مش عايزه ف اي صفحه» on the round ✓ / — badges of every list).
 */
export function BoolText({ value, locale }: { value: boolean; locale: 'ar' | 'en' }) {
  return value
    ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-success"><Check size={13} weight="bold" className="keep-color shrink-0" aria-hidden />{locale === 'ar' ? 'نعم' : 'Yes'}</span>
    : <span className="inline-flex items-center gap-1 text-xs text-fg-subtle"><X size={12} className="keep-color shrink-0" aria-hidden />{locale === 'ar' ? 'لا' : 'No'}</span>
}

export type SmartType = 'text' | 'number' | 'money' | 'date' | 'datetime' | 'mono' | 'bool'

/**
 * Renders a raw value by field name + type. Returns null when the caller should use its default.
 * `bare` drops the per-value leading icon and the two-line layout: the phone card already shows an
 * icon and a label beside every value, so repeating them there would only add noise.
 */
export function smartCell(key: string, value: unknown, type: SmartType, locale: 'ar' | 'en', first: boolean, bare = false): ReactNode {
  if (value === null || value === undefined || value === '') return <span className="text-fg-subtle">—</span>
  const k = key.replace(/[_\s]/g, '')
  const plain = (text: string, opts?: { mono?: boolean; dir?: 'ltr' }) => (
    <span className={cn('block truncate', opts?.mono && 'tabular')} dir={opts?.dir} title={text}>{text}</span>
  )
  switch (type) {
    case 'bool':
      return <BoolText value={Boolean(value)} locale={locale} />

    case 'money':
      return <MoneyText value={value} />
    case 'number':
      return <span className="tabular font-medium">{formatNumber(Number(value), locale)}</span>
    case 'date':
    case 'datetime':
      return <DateCell value={value} locale={locale} withTime={type === 'datetime'} bare={bare} />
    case 'mono':
      if (/^awb$/i.test(k)) return <span className="font-mono text-[13px] font-semibold tracking-wide text-fg-strong" dir="ltr">{String(value)}</span>
      return <span className="tabular text-fg-muted">{String(value)}</span>
  }
  const text = String(value)
  if (first && NAME_KEY.test(k)) return bare ? plain(text) : <NameCell name={text} />
  if (STATUS_KEY.test(k)) return <StatusPill>{text}</StatusPill>
  if (PHONE_KEY.test(k)) return bare ? plain(text, { mono: true, dir: 'ltr' }) : <IconText icon={Phone} mono dir="ltr">{text}</IconText>
  if (EMAIL_KEY.test(k)) return bare ? plain(text) : <IconText icon={EnvelopeSimple}>{text}</IconText>
  if (BRANCH_KEY.test(k)) return bare ? plain(text) : <IconText icon={Buildings}>{text}</IconText>
  if (CITY_KEY.test(k)) return bare ? plain(text) : <IconText icon={MapPin}>{text}</IconText>
  if (RUNNER_KEY.test(k)) return bare ? plain(text) : <IconText icon={Truck}>{text}</IconText>
  if (USER_KEY.test(k)) return bare ? plain(text) : <IconText icon={UserCircle}>{text}</IconText>
  if (TAG_KEY.test(k)) return bare ? plain(text) : <IconText icon={Tag}>{text}</IconText>
  if (NAME_KEY.test(k)) return <span className="truncate font-medium text-fg-strong">{text}</span>
  return null
}
